import './users-filters.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonPropsDrawer } from '@celebration/react/ClbDrawer/ClbDrawer.types';
import Drawer from '../../../../common/components/drawer';
import IUsersFiltersModel from '../../interfaces/IUsersFiltersModel';
import IUsersFilter from './interfaces/IUsersFilter';
import SelectAutocomplete from '../../../../common/components/select-autocomplete';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import {
    SULAHUB_ROLES,
    SULAHUB_USERS_TYPES,
} from '../../../../common/constants/roles';
import { ISelectData } from '../../../../common/components/select-autocomplete/interfaces/ISelectData';
import UsersFiltersService from './services/users-filters.service';
import authContext from '../../../../app/contexts/auth/auth.context';

function UsersFilters(props: Readonly<IUsersFilter>) {
    const { t } = useTranslation();
    const { setToast } = useToast();
    const { isThirdUser } = authContext.useAuth();
    const {
        currentFilters,
        onApplyFilter,
        isShowingFilters,
        toggleShowFilters,
        selectedCarriers,
        setSelectedCarriers,
    } = props;

    const normalizedCurrentFilters = useMemo(
        () =>
            ({
                ...currentFilters,
                status: currentFilters.status ?? [],
                types: currentFilters.types ?? [],
                carriersId: currentFilters.carriersId ?? [],
            }) as IUsersFiltersModel,
        [currentFilters],
    );

    const [filtersModel, setFiltersModel] =
        useState<IUsersFiltersModel>(normalizedCurrentFilters);
    const [carriers, setCarriers] = useState<ISelectData[]>([]);

    const status = [
        { value: '0', label: t('Inativo') },
        { value: '1', label: t('Ativo') },
    ];

    const fetchCarriersData = useCallback(async () => {
        if (!isThirdUser()) {
            const response = await UsersFiltersService.getCarriersList().catch(
                (error: Error) => {
                    setToast('negative', 'Erro', error.message);
                },
            );
            if (response) {
                setCarriers(response);
            }
        }
    }, [setToast, isThirdUser]);

    const handleCarrierSearch = async (
        value: string,
    ): Promise<ISelectData[]> => {
        const response = await UsersFiltersService.getCarriersList(value).catch(
            (error: Error) => {
                setToast('negative', 'Erro', error.message);
            },
        );

        return response ?? [];
    };

    const onCarriersResult = (data: ISelectData[]) => setCarriers([...data]);

    const carrierFilterOnChange = useCallback(
        (ids: string[]) => {
            const data = UsersFiltersService.carrierFilterOnChange(
                ids,
                carriers,
                selectedCarriers,
            );

            setSelectedCarriers(data);
            setFiltersModel({ ...filtersModel, carriersId: ids });
        },
        [carriers, filtersModel, selectedCarriers, setSelectedCarriers],
    );

    useEffect(() => {
        if (isShowingFilters && carriers.length === 0) {
            fetchCarriersData();
        }
    }, [carriers, fetchCarriersData, isShowingFilters]);

    useEffect(() => {
        if (isShowingFilters) {
            setFiltersModel(normalizedCurrentFilters);
        }
    }, [isShowingFilters, normalizedCurrentFilters]);

    const onClearFilters = useCallback(() => {
        setSelectedCarriers([]);
        setFiltersModel({
            status: [],
            carriersId: [],
            types: [],
            description: normalizedCurrentFilters.description,
        });
    }, [normalizedCurrentFilters.description, setSelectedCarriers]);

    const clearUsersFilterButton: ButtonPropsDrawer = {
        id: 'read-all-notification-button',
        key: 'user-filter-clear-button',
        label: t('Limpar'),
        disabled: false,
        loading: false,
        title: t('LimparFiltros'),
        onClick: onClearFilters,
        iconPosition: 'none',
        role: 'button',
        size: 'md',
        'data-testid': 'user-filter-clear-button',
        styleType: 'secondary',
    };

    const submitUsersFilterButton: ButtonPropsDrawer = {
        id: 'user-filter-submit-button',
        key: 'user-filter-submit-button',
        label: t('Filtrar'),
        disabled: false,
        loading: false,
        title: t('FiltrarUsuarios'),
        onClick: () => {
            onApplyFilter(filtersModel);
        },
        iconPosition: 'none',
        role: 'button',
        size: 'md',
        'data-testid': 'user-filter-submit-button',
        styleType: 'primary',
    };

    const filterButtonsList: [ButtonPropsDrawer, ButtonPropsDrawer?] = [
        clearUsersFilterButton,
        submitUsersFilterButton,
    ];

    return (
        <Drawer
            title={t('FiltrarUsuarios')}
            open={isShowingFilters}
            onClose={toggleShowFilters}
            buttonsFooter={filterButtonsList}
            data-testid="users-filters"
            description={t('FiltrarUsuarioDescricao')}
        >
            <>
                <div className="users-filter-empty-space" />
                <SelectAutocomplete
                    mode="multiple"
                    label={t('TipoUsuario')}
                    value={filtersModel.types}
                    onChange={(e: any) => {
                        setFiltersModel({ ...filtersModel, types: e });
                    }}
                    placeholder={t('Usuarios')}
                    dataTestId="users-filters-type"
                    getPopupContainer={node =>
                        (node.parentNode as HTMLElement) ?? document.body
                    }
                    options={
                        isThirdUser()
                            ? SULAHUB_USERS_TYPES.filter(
                                  t =>
                                      t.value ===
                                          SULAHUB_ROLES.SYSTEM_PROFILES
                                              .TP_USER ||
                                      t.value ===
                                          SULAHUB_ROLES.SYSTEM_PROFILES
                                              .TP_ADMIN_USER,
                              )
                            : SULAHUB_USERS_TYPES
                    }
                />
                {!isThirdUser() && (
                    <SelectAutocomplete
                        mode="multiple"
                        label={t('Transportadoras')}
                        value={selectedCarriers}
                        onChange={(e: any) => carrierFilterOnChange(e)}
                        placeholder={t('Transportadoras')}
                        dataTestId="users-filters-carrier"
                        options={carriers}
                        onSearch={handleCarrierSearch}
                        onFetchDataResults={onCarriersResult}
                        getPopupContainer={node =>
                            (node.parentNode as HTMLElement) ?? document.body
                        }
                    />
                )}
                <SelectAutocomplete
                    mode="multiple"
                    label={t('Status')}
                    value={filtersModel.status}
                    onChange={(e: any) => {
                        setFiltersModel({ ...filtersModel, status: e });
                    }}
                    placeholder={t('Status')}
                    dataTestId="users-filters-status"
                    options={status}
                    getPopupContainer={node =>
                        (node.parentNode as HTMLElement) ?? document.body
                    }
                />
            </>
        </Drawer>
    );
}

export default UsersFilters;
